import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAccount } from "wagmi";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { X402ReviewerIdentities } from "../components/x402/X402ReviewerIdentities";
import { X402ReviewerReviewList } from "../components/x402/X402ReviewerReviewList";
import {
  fetchWalletSent,
  formatBoonCompact,
  formatUsdc,
  shortAddr,
  type WalletSentResponse,
} from "../lib/api";
import {
  fetchX402ReviewerReviews,
  type X402ReviewerReviewsResponse,
} from "../lib/x402Discovery";

export function SenderPage() {
  const { address: rawAddress = "" } = useParams();
  const address = rawAddress.toLowerCase();
  const [data, setData] = useState<WalletSentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [x402History, setX402History] = useState<X402ReviewerReviewsResponse | null>(null);
  const { address: connected } = useAccount();
  const connectedIsSender = Boolean(
    connected && address && connected.toLowerCase() === address.toLowerCase(),
  );

  useEffect(() => {
    if (!address) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchWalletSent(address)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [address]);

  useEffect(() => {
    let cancelled = false;
    setX402History(null);
    if (!address) return () => { cancelled = true; };
    fetchX402ReviewerReviews(address, 3)
      .then((result) => {
        if (!cancelled) setX402History(result);
      })
      .catch(() => {
        if (!cancelled) setX402History(null);
      });
    return () => { cancelled = true; };
  }, [address]);

  const tipper = data?.tipper ?? null;

  return (
    <>
      <Nav current="board" />
      <main className="px-6 md:px-10 pt-12 md:pt-24 max-w-3xl mx-auto">
        <header className="mb-10 space-y-3 animate-fade-up">
          <p className="text-sm text-muted btn-mono tracking-wide uppercase">Sender profile</p>
          <h1 className="text-3xl md:text-4xl font-display tracking-tight leading-[1.05] break-all">
            <a
              href={`https://basescan.org/address/${address}`}
              target="_blank"
              rel="noreferrer"
              className="hover:underline"
            >
              {shortAddr(address)} ↗
            </a>
          </h1>
          {connectedIsSender && (
            <p className="text-sm text-muted">
              Connected as this wallet. To read a private tip again, open its receipt and select
              Unlock. The original tipper and recipient read free after EIP-712 authorization.
            </p>
          )}
          {x402History && (
            <X402ReviewerIdentities
              accounts={x402History.reviewer.accounts}
              resolution={x402History.identityResolution}
            />
          )}
        </header>

        {loading && <Panel message="Loading…" />}
        {!loading && error && <Panel message="Could not load sender profile." sub={error} />}
        {!loading && !error && data?.note && <Panel message={data.note} />}

        {!loading && !error && tipper && (
          <section className="card p-6 md:p-8 mb-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            <Stat label="Total sent" value={formatUsdc(tipper.totalSent)} />
            <Stat label="Boons sent" value={String(tipper.tipCount)} />
            <Stat label="Private tips" value={String(tipper.privateTipCount ?? 0)} />
            <Stat
              label="$BOON burn"
              value={formatBoonCompact(tipper.boonBurnedForPrivacy)}
            />
          </section>
        )}

        {!loading && !error && tipper && (
          <section className="card p-6 md:p-8 space-y-3">
            <h2 className="text-xl font-display tracking-tight text-ink">Tip history</h2>
            <p className="text-sm text-muted">
              Individual tips are available through the paid x402 graph. The original tipper or
              recipient can unlock private details free from each receipt.
            </p>
          </section>
        )}

        {x402History && x402History.items.length > 0 && (
          <section className="mt-10">
            <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">x402 recognition</p>
                <h2 className="mt-2 text-2xl font-display tracking-tight">Reviews from this wallet</h2>
              </div>
              <Link className="min-h-11 inline-flex items-center btn-mono text-xs text-olive-deep hover:underline" to={`/x402/reviewers/${address}`}>
                View full review history →
              </Link>
            </div>
            <X402ReviewerReviewList items={x402History.items} />
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted uppercase tracking-wide">{label}</p>
      <p className="num text-2xl text-ink mt-1">{value}</p>
    </div>
  );
}

function Panel({ message, sub }: { message: string; sub?: string }) {
  return (
    <div className="card p-8 text-center">
      <p className="text-ink">{message}</p>
      {sub && <p className="text-sm text-muted mt-2">{sub}</p>}
    </div>
  );
}
